import { Copy, Download, FilePenLine, FileText, Trash2 } from 'lucide-react';

import { Button } from '../../../../components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../../../components/ui/tooltip';
import { cn } from '../../../../lib/utils';
import type { MediaEntry } from '../../lib/mediaEntries';

interface DocumentCardProps {
  accent: 'brand' | 'error';
  entry: MediaEntry;
  isDeleting: boolean;
  onDownload: () => void;
  onRequestDuplicate: () => void;
  onRequestDelete: () => void;
  onRequestRename: () => void;
}

const DocumentCard = ({
  accent,
  entry,
  isDeleting,
  onDownload,
  onRequestDuplicate,
  onRequestDelete,
  onRequestRename,
}: DocumentCardProps) => (
  <article className="overflow-hidden rounded-lg border border-border-subtle bg-bg-surface">
    <div
      className={cn(
        'flex h-[100px] flex-col items-center justify-center gap-2',
        accent === 'error' ? 'bg-error-light text-error' : 'bg-surface-info text-brand-vivid'
      )}
    >
      <FileText className="size-8" />
      <span className="text-[10px] font-bold uppercase tracking-[0.1em]">PDF</span>
    </div>
    <div className="flex flex-col gap-2 px-3.5 py-3">
      <Tooltip>
        <TooltipTrigger asChild>
          <p className="m-0 min-w-0 cursor-default truncate text-xs font-bold text-text-default">{entry.name}</p>
        </TooltipTrigger>
        <TooltipContent className="max-w-72 break-words">{entry.name}</TooltipContent>
      </Tooltip>
      <div className="h-px bg-border-subtle" />
      <p className="m-0 truncate text-xs text-text-muted">{entry.bucketFilePath}</p>
      <div className="flex min-h-7 items-center justify-end gap-2 pt-2">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button
                aria-label={`Descargar ${entry.name}`}
                disabled={!entry.publicFileURL}
                onClick={onDownload}
                size="icon-xs"
                type="button"
                variant="ghost"
              >
                <Download className="size-4 text-text-muted" />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>Descargar</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button
                aria-label={`Editar nombre de ${entry.name}`}
                disabled={isDeleting}
                onClick={onRequestRename}
                size="icon-xs"
                type="button"
                variant="ghost"
              >
                <FilePenLine className="size-4 text-text-muted" />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>Editar nombre</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button
                aria-label={`Duplicar ${entry.name}`}
                disabled={isDeleting}
                onClick={onRequestDuplicate}
                size="icon-xs"
                type="button"
                variant="ghost"
              >
                <Copy className="size-4 text-text-muted" />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>Duplicar</TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Button
                aria-label={`Eliminar ${entry.name}`}
                disabled={isDeleting}
                onClick={onRequestDelete}
                size="icon-xs"
                type="button"
                variant="ghost"
              >
                <Trash2 className="size-4 text-error" />
              </Button>
            </span>
          </TooltipTrigger>
          <TooltipContent>Eliminar</TooltipContent>
        </Tooltip>
      </div>
    </div>
  </article>
);

export default DocumentCard;
